import React, { useState, useMemo, useEffect, useRef } from 'react';

export interface Column<T> {
  key: string;
  header: React.ReactNode;
  title?: string; // String representation for column visibility menu and CSV export
  sortable?: boolean;
  align?: 'left' | 'center' | 'right';
  width?: string;
  minWidth?: string;
  className?: string;
  headerClassName?: string;
  render?: (row: T, index: number) => React.ReactNode;
  sortValue?: (row: T) => string | number | boolean | Date;
  hideable?: boolean; // Can this column be hidden? (default: true)
  defaultVisible?: boolean; // Is this column initially visible? (default: true)
}

export interface DataTableProps<T> {
  data: T[];
  columns: Column<T>[];
  getRowId?: (row: T, index: number) => string | number;

  // Header & Toolbar
  title?: React.ReactNode;
  subtitle?: React.ReactNode;
  actions?: React.ReactNode;
  searchable?: boolean;
  searchPlaceholder?: string;
  searchFields?: (keyof T | string)[];
  searchValue?: string;
  onSearchChange?: (val: string) => void;

  // Column Visibility ("Select Column")
  columnVisibility?: boolean;
  defaultHiddenColumns?: string[];

  // Sorting
  defaultSortKey?: string;
  defaultSortDir?: 'asc' | 'desc';
  sortKey?: string;
  sortDir?: 'asc' | 'desc';
  onSortChange?: (key: string, dir: 'asc' | 'desc') => void;

  // Selection ("Select Row")
  selectable?: boolean;
  selectedIds?: (string | number)[];
  onSelectionChange?: (selectedIds: (string | number)[], selectedRows: T[]) => void;
  bulkActions?: (selectedRows: T[], clearSelection: () => void) => React.ReactNode;

  // Row Interaction
  onRowClick?: (row: T) => void;
  isRowActive?: (row: T) => boolean;
  rowClassName?: (row: T, index: number) => string;

  // Density & Styling
  density?: 'compact' | 'normal' | 'spacious';
  striped?: boolean;

  // Pagination
  pagination?: boolean;
  pageSize?: number;
  pageSizeOptions?: number[];

  // Loading & Empty
  loading?: boolean;
  emptyIcon?: string;
  emptyTitle?: string;
  emptyDescription?: string;
  emptyAction?: React.ReactNode;

  // Features
  exportable?: boolean;
  exportFilename?: string;
  className?: string;
  minWidth?: string;
}

export default function DataTable<T extends Record<string, any>>({
  data,
  columns,
  getRowId = (row: T, idx: number) => row.id ?? row._id ?? row.key ?? idx,

  // Toolbar
  title,
  subtitle,
  actions,
  searchable = false,
  searchPlaceholder = 'Search records...',
  searchFields,
  searchValue: externalSearch,
  onSearchChange,

  // Column Visibility
  columnVisibility = true,
  defaultHiddenColumns = [],

  // Sorting
  defaultSortKey,
  defaultSortDir = 'asc',
  sortKey: controlledSortKey,
  sortDir: controlledSortDir,
  onSortChange,

  // Selection
  selectable = false,
  selectedIds: controlledSelectedIds,
  onSelectionChange,
  bulkActions,

  // Row Click & Styling
  onRowClick,
  isRowActive,
  rowClassName,
  density = 'normal',
  striped = false,

  // Pagination
  pagination = true,
  pageSize: initialPageSize = 10,
  pageSizeOptions = [10, 25, 50],

  // Loading & Empty
  loading = false,
  emptyIcon = 'ri-search-eye-line',
  emptyTitle = 'No records found',
  emptyDescription = 'Try adjusting your search or filters to find what you are looking for.',
  emptyAction,

  // Features
  exportable = false,
  exportFilename = 'export.csv',
  className = '',
  minWidth = '800px',
}: DataTableProps<T>) {
  // 1. Column Visibility State
  const [hiddenColumnKeys, setHiddenColumnKeys] = useState<string[]>(() => {
    const initialHidden = new Set(defaultHiddenColumns);
    columns.forEach((c) => {
      if (c.defaultVisible === false) {
        initialHidden.add(c.key);
      }
    });
    return Array.from(initialHidden);
  });

  const [columnMenuOpen, setColumnMenuOpen] = useState(false);
  const [columnFilterSearch, setColumnFilterSearch] = useState('');
  const columnMenuRef = useRef<HTMLDivElement>(null);

  // Click outside to close column menu
  useEffect(() => {
    if (!columnMenuOpen) return;
    const handleClickOutside = (event: MouseEvent) => {
      if (columnMenuRef.current && !columnMenuRef.current.contains(event.target as Node)) {
        setColumnMenuOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [columnMenuOpen]);

  // Visible columns computed list
  const visibleColumns = useMemo(() => {
    return columns.filter((col) => !hiddenColumnKeys.includes(col.key));
  }, [columns, hiddenColumnKeys]);

  const toggleColumnVisibility = (key: string) => {
    setHiddenColumnKeys((prev) => {
      if (prev.includes(key)) {
        return prev.filter((k) => k !== key);
      } else {
        // Prevent hiding all columns (must have at least 1 visible)
        if (visibleColumns.length <= 1) return prev;
        return [...prev, key];
      }
    });
  };

  const showAllColumns = () => {
    setHiddenColumnKeys([]);
  };

  const resetColumnsToDefault = () => {
    const initialHidden = new Set(defaultHiddenColumns);
    columns.forEach((c) => {
      if (c.defaultVisible === false) {
        initialHidden.add(c.key);
      }
    });
    setHiddenColumnKeys(Array.from(initialHidden));
  };

  // 2. Internal Search
  const [internalSearch, setInternalSearch] = useState('');
  const search = externalSearch !== undefined ? externalSearch : internalSearch;
  const handleSearch = (val: string) => {
    if (onSearchChange) onSearchChange(val);
    else setInternalSearch(val);
  };

  // 3. Internal Sorting
  const [internalSortKey, setInternalSortKey] = useState<string>(defaultSortKey || '');
  const [internalSortDir, setInternalSortDir] = useState<'asc' | 'desc'>(defaultSortDir);
  const activeSortKey = controlledSortKey !== undefined ? controlledSortKey : internalSortKey;
  const activeSortDir = controlledSortDir !== undefined ? controlledSortDir : internalSortDir;

  const handleHeaderSort = (key: string) => {
    const nextDir = activeSortKey === key ? (activeSortDir === 'asc' ? 'desc' : 'asc') : 'asc';
    if (onSortChange) {
      onSortChange(key, nextDir);
    } else {
      setInternalSortKey(key);
      setInternalSortDir(nextDir);
    }
  };

  // 4. Internal Selection
  const [internalSelectedIds, setInternalSelectedIds] = useState<(string | number)[]>([]);
  const selectedIds = controlledSelectedIds !== undefined ? controlledSelectedIds : internalSelectedIds;

  const handleToggleSelectRow = (id: string | number) => {
    const next = selectedIds.includes(id) ? selectedIds.filter((item) => item !== id) : [...selectedIds, id];
    if (onSelectionChange) {
      const selectedRows = data.filter((r, i) => next.includes(getRowId(r, i)));
      onSelectionChange(next, selectedRows);
    } else {
      setInternalSelectedIds(next);
    }
  };

  const handleToggleSelectAll = (currentPageRows: T[]) => {
    const pageIds = currentPageRows.map((r, i) => getRowId(r, i));
    const allSelected = pageIds.length > 0 && pageIds.every((id) => selectedIds.includes(id));
    let next: (string | number)[];
    if (allSelected) {
      next = selectedIds.filter((id) => !pageIds.includes(id));
    } else {
      const merged = new Set([...selectedIds, ...pageIds]);
      next = Array.from(merged);
    }
    if (onSelectionChange) {
      const selectedRows = data.filter((r, i) => next.includes(getRowId(r, i)));
      onSelectionChange(next, selectedRows);
    } else {
      setInternalSelectedIds(next);
    }
  };

  const handleSelectAllTotal = () => {
    const allIds = data.map((r, i) => getRowId(r, i));
    if (onSelectionChange) {
      onSelectionChange(allIds, [...data]);
    } else {
      setInternalSelectedIds(allIds);
    }
  };

  const clearSelection = () => {
    if (onSelectionChange) onSelectionChange([], []);
    else setInternalSelectedIds([]);
  };

  // 5. Internal Pagination
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(initialPageSize);

  // Reset page when data length or search changes
  useEffect(() => {
    setPage(1);
  }, [data.length, search]);

  // 6. Filter Data
  const filteredData = useMemo(() => {
    if (!search.trim()) return data;
    const q = search.toLowerCase().trim();
    return data.filter((row) => {
      if (searchFields && searchFields.length > 0) {
        return searchFields.some((field) => {
          const val = row[field as keyof T];
          return val !== undefined && val !== null && String(val).toLowerCase().includes(q);
        });
      }
      return Object.values(row).some((val) => {
        if (typeof val === 'object' && val !== null) {
          return Object.values(val).some((sub) => String(sub).toLowerCase().includes(q));
        }
        return val !== undefined && val !== null && String(val).toLowerCase().includes(q);
      });
    });
  }, [data, search, searchFields]);

  // 7. Sort Data
  const sortedData = useMemo(() => {
    if (!activeSortKey) return filteredData;
    const col = columns.find((c) => c.key === activeSortKey);
    return [...filteredData].sort((a, b) => {
      let valA = col?.sortValue ? col.sortValue(a) : a[activeSortKey];
      let valB = col?.sortValue ? col.sortValue(b) : b[activeSortKey];

      if (valA === valB) return 0;
      if (valA === undefined || valA === null) return 1;
      if (valB === undefined || valB === null) return -1;

      let result = 0;
      if (typeof valA === 'number' && typeof valB === 'number') {
        result = valA - valB;
      } else {
        result = String(valA).localeCompare(String(valB), undefined, { numeric: true, sensitivity: 'base' });
      }
      return activeSortDir === 'asc' ? result : -result;
    });
  }, [filteredData, activeSortKey, activeSortDir, columns]);

  // 8. Paginate Data
  const totalItems = sortedData.length;
  const totalPages = Math.max(1, Math.ceil(totalItems / pageSize));
  const safePage = Math.min(Math.max(1, page), totalPages);

  const paginatedData = useMemo(() => {
    if (!pagination) return sortedData;
    const start = (safePage - 1) * pageSize;
    return sortedData.slice(start, start + pageSize);
  }, [sortedData, pagination, safePage, pageSize]);

  // Selection Checkbox State for Header
  const headerCheckboxRef = useRef<HTMLInputElement>(null);
  const currentPageIds = paginatedData.map((r, i) => getRowId(r, i));
  const isCurrentPageAllSelected =
    currentPageIds.length > 0 && currentPageIds.every((id) => selectedIds.includes(id));
  const isCurrentPageSomeSelected =
    currentPageIds.some((id) => selectedIds.includes(id)) && !isCurrentPageAllSelected;

  useEffect(() => {
    if (headerCheckboxRef.current) {
      headerCheckboxRef.current.indeterminate = isCurrentPageSomeSelected;
    }
  }, [isCurrentPageSomeSelected]);

  // CSV Export Function (only exports visible columns, respects filter/sort or selection)
  const exportToCsv = (onlySelected: boolean = false) => {
    const targetRows = onlySelected
      ? data.filter((r, i) => selectedIds.includes(getRowId(r, i)))
      : sortedData;

    if (targetRows.length === 0) return;

    const headers = visibleColumns.map((c) => {
      if (c.title) return c.title;
      if (typeof c.header === 'string') return c.header;
      return c.key.charAt(0).toUpperCase() + c.key.slice(1);
    });

    const rows = targetRows.map((row) =>
      visibleColumns.map((c) => {
        const val = c.sortValue ? c.sortValue(row) : row[c.key];
        const str = String(val ?? '').replace(/"/g, '""');
        return `"${str}"`;
      })
    );

    const csvContent = [headers.join(','), ...rows.map((r) => r.join(','))].join('\n');
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', exportFilename);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  // Density padding classes
  const densityCellPadding = {
    compact: 'px-4 py-2 text-xs',
    normal: 'px-5 py-3.5 text-sm',
    spacious: 'px-6 py-4.5 text-sm',
  }[density];

  const densityHeaderPadding = {
    compact: 'px-4 py-2.5',
    normal: 'px-5 py-3.5',
    spacious: 'px-6 py-4',
  }[density];

  // Helper for column label in visibility menu
  const getColumnLabel = (col: Column<T>) => {
    if (col.title) return col.title;
    if (typeof col.header === 'string') return col.header;
    return col.key.charAt(0).toUpperCase() + col.key.slice(1);
  };

  return (
    <div
      className={`bg-background-50/95 border border-background-200/90 rounded-2xl shadow-sm overflow-hidden flex flex-col transition-all ${className}`}
    >
      {/* 1. Header Toolbar (Title, Search, Column Visibility, Export, Actions) */}
      {(title || subtitle || searchable || exportable || columnVisibility || actions) && (
        <div className="px-5 py-4 border-b border-background-200/80 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3.5 bg-background-50/50 backdrop-blur-xs">
          {/* Title & Badge */}
          <div className="min-w-0">
            {title && (
              <div className="flex items-center gap-2.5">
                <h3 className="font-semibold text-base md:text-lg text-foreground-900 tracking-tight truncate">
                  {title}
                </h3>
                <span className="text-xs font-medium px-2.5 py-0.5 rounded-full bg-primary-50 text-primary-700 border border-primary-200/60 shadow-2xs">
                  {totalItems.toLocaleString()}
                </span>
              </div>
            )}
            {subtitle && <p className="text-xs text-foreground-500 mt-0.5 truncate">{subtitle}</p>}
          </div>

          {/* Right Toolbar Controls */}
          <div className="flex items-center flex-wrap gap-2.5">
            {/* Search Input */}
            {searchable && (
              <div className="relative flex-1 sm:w-64 min-w-[200px]">
                <i className="ri-search-line absolute left-3 top-1/2 -translate-y-1/2 text-foreground-400 text-sm pointer-events-none"></i>
                <input
                  type="text"
                  value={search}
                  onChange={(e) => handleSearch(e.target.value)}
                  placeholder={searchPlaceholder}
                  className="w-full pl-9 pr-8 py-2 text-xs md:text-sm bg-background-100/70 border border-background-200/90 rounded-xl text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-primary-500 focus:bg-background-50 focus:ring-2 focus:ring-primary-500/15 transition-all shadow-2xs"
                />
                {search && (
                  <button
                    type="button"
                    onClick={() => handleSearch('')}
                    className="absolute right-2.5 top-1/2 -translate-y-1/2 text-foreground-400 hover:text-foreground-700 transition-colors cursor-pointer"
                    aria-label="Clear search"
                  >
                    <i className="ri-close-circle-fill text-sm"></i>
                  </button>
                )}
              </div>
            )}

            {/* Column Visibility Menu Button */}
            {columnVisibility && columns.length > 0 && (
              <div className="relative" ref={columnMenuRef}>
                <button
                  type="button"
                  onClick={() => setColumnMenuOpen((prev) => !prev)}
                  className={`inline-flex items-center gap-2 px-3 py-2 rounded-xl border text-xs font-semibold transition-all duration-150 cursor-pointer shadow-2xs ${
                    columnMenuOpen || hiddenColumnKeys.length > 0
                      ? 'bg-primary-50 border-primary-300 text-primary-700 ring-2 ring-primary-500/15'
                      : 'bg-background-50 hover:bg-background-100/80 border-background-200/90 text-foreground-700 hover:text-foreground-950'
                  }`}
                  title="Toggle visible columns"
                  aria-expanded={columnMenuOpen}
                >
                  <i className="ri-layout-column-line text-sm text-primary-500"></i>
                  <span>Columns</span>
                  <span
                    className={`px-1.5 py-0.5 rounded-full text-[10px] font-bold ${
                      hiddenColumnKeys.length > 0
                        ? 'bg-primary-500 text-white'
                        : 'bg-background-200/80 text-foreground-600'
                    }`}
                  >
                    {visibleColumns.length}/{columns.length}
                  </span>
                  <i
                    className={`ri-arrow-down-s-line text-xs transition-transform duration-200 ${
                      columnMenuOpen ? 'rotate-180' : ''
                    }`}
                  ></i>
                </button>

                {/* Dropdown Menu Popover */}
                {columnMenuOpen && (
                  <div className="absolute right-0 top-full mt-2 w-72 bg-background-50 border border-background-200 rounded-2xl shadow-xl z-40 p-3 flex flex-col gap-2.5 animate-scale-in">
                    {/* Header */}
                    <div className="flex items-center justify-between pb-2 border-b border-background-200/80">
                      <div>
                        <h4 className="text-xs font-bold text-foreground-950">Customize Columns</h4>
                        <p className="text-[11px] text-foreground-500">Show or hide table columns</p>
                      </div>
                      {hiddenColumnKeys.length > 0 && (
                        <button
                          type="button"
                          onClick={resetColumnsToDefault}
                          className="text-[11px] font-semibold text-primary-600 hover:text-primary-800 transition-colors cursor-pointer"
                        >
                          Reset
                        </button>
                      )}
                    </div>

                    {/* Filter search if many columns */}
                    {columns.length > 6 && (
                      <div className="relative">
                        <i className="ri-search-line absolute left-2.5 top-1/2 -translate-y-1/2 text-foreground-400 text-xs"></i>
                        <input
                          type="text"
                          value={columnFilterSearch}
                          onChange={(e) => setColumnFilterSearch(e.target.value)}
                          placeholder="Filter columns..."
                          className="w-full pl-7 pr-3 py-1.5 text-xs bg-background-100/70 border border-background-200 rounded-lg text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400"
                        />
                      </div>
                    )}

                    {/* Column List */}
                    <div className="max-h-56 overflow-y-auto custom-scrollbar flex flex-col gap-1 pr-1">
                      {columns
                        .filter((col) => {
                          if (!columnFilterSearch.trim()) return true;
                          const label = getColumnLabel(col).toLowerCase();
                          return label.includes(columnFilterSearch.toLowerCase().trim());
                        })
                        .map((col) => {
                          const isVisible = !hiddenColumnKeys.includes(col.key);
                          const isHideable = col.hideable !== false;
                          const isLastVisible = isVisible && visibleColumns.length <= 1;
                          const isDisabled = !isHideable || isLastVisible;

                          return (
                            <label
                              key={col.key}
                              className={`flex items-center justify-between px-2.5 py-1.5 rounded-lg text-xs transition-colors cursor-pointer select-none ${
                                isVisible ? 'bg-primary-50/50 hover:bg-primary-50/90 text-foreground-900' : 'hover:bg-background-100 text-foreground-500'
                              } ${isDisabled ? 'opacity-60 cursor-not-allowed' : ''}`}
                            >
                              <div className="flex items-center gap-2.5 min-w-0">
                                <input
                                  type="checkbox"
                                  checked={isVisible}
                                  disabled={isDisabled}
                                  onChange={() => toggleColumnVisibility(col.key)}
                                  className="w-4 h-4 rounded text-primary-600 border-background-300 focus:ring-primary-500/20 cursor-pointer"
                                />
                                <span className="font-medium truncate">{getColumnLabel(col)}</span>
                              </div>
                              {!isHideable && (
                                <span className="text-[10px] text-foreground-400 uppercase tracking-wider font-semibold">
                                  Locked
                                </span>
                              )}
                            </label>
                          );
                        })}
                    </div>

                    {/* Footer Actions */}
                    <div className="pt-2 border-t border-background-200/80 flex items-center justify-between text-[11px]">
                      <button
                        type="button"
                        onClick={showAllColumns}
                        className="text-foreground-600 hover:text-foreground-950 font-semibold transition-colors cursor-pointer"
                      >
                        Show all ({columns.length})
                      </button>
                      <button
                        type="button"
                        onClick={() => setColumnMenuOpen(false)}
                        className="px-2.5 py-1 rounded-lg bg-background-200/70 hover:bg-background-200 text-foreground-800 font-bold transition-colors cursor-pointer"
                      >
                        Done
                      </button>
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* CSV Export Button */}
            {exportable && (
              <button
                type="button"
                onClick={() => exportToCsv(false)}
                className="inline-flex items-center gap-1.5 px-3 py-2 rounded-xl bg-background-50 hover:bg-background-100/80 border border-background-200/90 text-foreground-700 hover:text-foreground-950 text-xs font-semibold transition-all duration-150 cursor-pointer shadow-2xs"
                title="Export data to CSV"
              >
                <i className="ri-download-2-line text-sm text-primary-500"></i>
                <span className="hidden sm:inline">Export CSV</span>
              </button>
            )}

            {/* Custom Action buttons slot */}
            {actions}
          </div>
        </div>
      )}

      {/* 2. Floating / Docked Bulk Selection Bar */}
      {selectable && selectedIds.length > 0 && (
        <div className="bg-gradient-to-r from-primary-50 via-primary-50/80 to-background-50 border-b border-primary-200/80 px-5 py-2.5 flex flex-wrap items-center justify-between gap-3 text-xs animate-fade-in shadow-2xs">
          <div className="flex items-center gap-2.5">
            <span className="w-5 h-5 rounded-full bg-primary-500 text-white flex items-center justify-center font-bold text-[11px] shadow-2xs">
              {selectedIds.length}
            </span>
            <span className="font-semibold text-primary-950">
              {selectedIds.length} row{selectedIds.length > 1 ? 's' : ''} selected
            </span>
            <div className="h-4 w-px bg-primary-200 mx-1"></div>

            {totalItems > paginatedData.length && selectedIds.length < totalItems && (
              <button
                type="button"
                onClick={handleSelectAllTotal}
                className="text-primary-700 hover:text-primary-900 underline font-semibold transition-colors cursor-pointer"
              >
                Select all {totalItems} total records
              </button>
            )}

            <button
              type="button"
              onClick={clearSelection}
              className="text-foreground-500 hover:text-foreground-900 font-medium transition-colors cursor-pointer"
            >
              Clear selection
            </button>
          </div>

          <div className="flex items-center gap-2">
            {bulkActions ? (
              bulkActions(
                data.filter((r, i) => selectedIds.includes(getRowId(r, i))),
                clearSelection
              )
            ) : (
              <button
                type="button"
                onClick={() => exportToCsv(true)}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-background-50 border border-primary-200 text-primary-800 hover:bg-primary-100 text-xs font-semibold transition-colors cursor-pointer shadow-2xs"
              >
                <i className="ri-download-2-line text-sm text-primary-600"></i>
                Export selected ({selectedIds.length})
              </button>
            )}
          </div>
        </div>
      )}

      {/* 3. Table Container */}
      <div className="overflow-x-auto custom-scrollbar relative">
        <table className="w-full text-sm border-collapse" style={{ minWidth }}>
          {/* Table Header */}
          <thead>
            <tr className="bg-background-100/80 backdrop-blur-xs border-b border-background-200/90 text-left">
              {selectable && (
                <th className={`w-12 text-center select-none ${densityHeaderPadding}`}>
                  <input
                    ref={headerCheckboxRef}
                    type="checkbox"
                    checked={isCurrentPageAllSelected}
                    onChange={() => handleToggleSelectAll(paginatedData)}
                    className="w-4 h-4 rounded text-primary-600 border-background-300 focus:ring-primary-500/20 cursor-pointer"
                    aria-label="Select all rows on current page"
                  />
                </th>
              )}

              {visibleColumns.map((col) => {
                const isSorted = activeSortKey === col.key;
                const alignClass =
                  col.align === 'right' ? 'text-right' : col.align === 'center' ? 'text-center' : 'text-left';

                return (
                  <th
                    key={col.key}
                    style={{ width: col.width, minWidth: col.minWidth }}
                    className={`${densityHeaderPadding} text-[11px] font-semibold uppercase tracking-wider text-foreground-500 whitespace-nowrap select-none ${alignClass} ${
                      col.headerClassName || ''
                    }`}
                  >
                    {col.sortable ? (
                      <button
                        type="button"
                        onClick={() => handleHeaderSort(col.key)}
                        className={`inline-flex items-center gap-1.5 transition-colors cursor-pointer group ${
                          isSorted ? 'text-primary-600 font-semibold' : 'hover:text-foreground-800'
                        } ${col.align === 'right' ? 'flex-row-reverse ml-auto' : ''}`}
                      >
                        <span>{col.header}</span>
                        <span className="w-4 h-4 inline-flex items-center justify-center">
                          {isSorted ? (
                            <i
                              className={`${
                                activeSortDir === 'asc' ? 'ri-arrow-up-line' : 'ri-arrow-down-line'
                              } text-sm text-primary-500 font-semibold transition-transform duration-150`}
                            ></i>
                          ) : (
                            <i className="ri-arrow-up-down-line text-xs text-foreground-400 group-hover:text-foreground-600 opacity-60"></i>
                          )}
                        </span>
                      </button>
                    ) : (
                      <span>{col.header}</span>
                    )}
                  </th>
                );
              })}
            </tr>
          </thead>

          {/* Table Body */}
          <tbody className={`divide-y divide-background-200/70 ${striped ? 'even:bg-background-100/30' : ''}`}>
            {loading ? (
              // Loading Skeleton Rows
              Array.from({ length: pageSize }).map((_, rIdx) => (
                <tr key={`skeleton-${rIdx}`} className="animate-pulse">
                  {selectable && (
                    <td className="px-4 py-3.5 text-center">
                      <div className="w-4 h-4 bg-background-200 rounded mx-auto"></div>
                    </td>
                  )}
                  {visibleColumns.map((col, cIdx) => (
                    <td key={`skeleton-col-${cIdx}`} className={densityCellPadding}>
                      <div
                        className="h-4 bg-background-200 rounded-md"
                        style={{ width: `${Math.floor(40 + (cIdx * 19) % 55)}%` }}
                      ></div>
                    </td>
                  ))}
                </tr>
              ))
            ) : paginatedData.length === 0 ? (
              // Empty State
              <tr>
                <td
                  colSpan={visibleColumns.length + (selectable ? 1 : 0)}
                  className="px-6 py-16 text-center bg-background-50/40"
                >
                  <div className="max-w-md mx-auto flex flex-col items-center">
                    <span className="w-14 h-14 flex items-center justify-center rounded-2xl bg-primary-50 text-primary-500 mb-3.5 shadow-inner">
                      <i className={`${emptyIcon} text-2xl`}></i>
                    </span>
                    <h4 className="font-heading font-bold text-base text-foreground-950">{emptyTitle}</h4>
                    <p className="text-xs sm:text-sm text-foreground-500 mt-1 leading-relaxed">
                      {emptyDescription}
                    </p>
                    {search && (
                      <button
                        type="button"
                        onClick={() => handleSearch('')}
                        className="mt-4 px-3.5 py-1.5 rounded-xl bg-primary-50 text-primary-700 hover:bg-primary-100 text-xs font-bold transition-colors cursor-pointer shadow-2xs"
                      >
                        Clear search filter
                      </button>
                    )}
                    {emptyAction && <div className="mt-4">{emptyAction}</div>}
                  </div>
                </td>
              </tr>
            ) : (
              // Real Data Rows
              paginatedData.map((row, idx) => {
                const rowId = getRowId(row, idx);
                const isSelected = selectedIds.includes(rowId);
                const isActive = isRowActive ? isRowActive(row) : false;
                const customClasses = rowClassName ? rowClassName(row, idx) : '';

                return (
                  <tr
                    key={String(rowId)}
                    onClick={() => onRowClick && onRowClick(row)}
                    className={`transition-colors duration-150 group relative ${
                      isSelected
                        ? 'bg-primary-50/60 hover:bg-primary-50/80 shadow-2xs'
                        : isActive
                        ? 'bg-primary-50/40 hover:bg-primary-50/70'
                        : 'hover:bg-primary-50/20'
                    } ${onRowClick ? 'cursor-pointer' : ''} ${customClasses}`}
                  >
                    {selectable && (
                      <td
                        className="w-12 text-center align-middle"
                        onClick={(e) => e.stopPropagation()}
                      >
                        <input
                          type="checkbox"
                          checked={isSelected}
                          onChange={() => handleToggleSelectRow(rowId)}
                          className="w-4 h-4 rounded text-primary-600 border-background-300 focus:ring-primary-500/20 cursor-pointer"
                          aria-label={`Select row ${rowId}`}
                        />
                      </td>
                    )}

                    {visibleColumns.map((col) => {
                      const alignClass =
                        col.align === 'right' ? 'text-right' : col.align === 'center' ? 'text-center' : 'text-left';

                      return (
                        <td
                          key={col.key}
                          className={`${densityCellPadding} align-middle ${alignClass} ${col.className || ''}`}
                        >
                          {col.render ? col.render(row, idx) : (row[col.key] ?? '—')}
                        </td>
                      );
                    })}
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>

      {/* 4. Table Footer / Pagination */}
      {pagination && !loading && totalItems > 0 && (
        <div className="px-5 py-3.5 border-t border-background-200/80 bg-background-50/70 flex flex-col sm:flex-row items-center justify-between gap-3 text-xs text-foreground-500">
          {/* Entries summary & page size selector */}
          <div className="flex items-center gap-3">
            <p>
              Showing <span className="font-semibold text-foreground-900">{(safePage - 1) * pageSize + 1}</span> to{' '}
              <span className="font-semibold text-foreground-900">{Math.min(safePage * pageSize, totalItems)}</span> of{' '}
              <span className="font-semibold text-foreground-900">{totalItems.toLocaleString()}</span> entries
            </p>

            {pageSizeOptions.length > 1 && (
              <div className="hidden sm:flex items-center gap-1.5 ml-2">
                <span className="text-foreground-400">Rows:</span>
                <select
                  value={pageSize}
                  onChange={(e) => {
                    setPageSize(Number(e.target.value));
                    setPage(1);
                  }}
                  className="bg-background-100/80 border border-background-200 rounded-lg px-2 py-1 text-xs text-foreground-800 focus:outline-none focus:border-primary-400 cursor-pointer"
                >
                  {pageSizeOptions.map((opt) => (
                    <option key={opt} value={opt}>
                      {opt}
                    </option>
                  ))}
                </select>
              </div>
            )}
          </div>

          {/* Page numbers navigation */}
          <div className="flex items-center gap-1">
            <button
              type="button"
              onClick={() => setPage((p) => Math.max(1, p - 1))}
              disabled={safePage <= 1}
              className="w-8 h-8 flex items-center justify-center rounded-xl border border-background-200/90 text-foreground-600 hover:bg-background-100 disabled:opacity-30 disabled:cursor-not-allowed transition-colors cursor-pointer"
              title="Previous page"
              aria-label="Previous page"
            >
              <i className="ri-arrow-left-s-line text-sm"></i>
            </button>

            {/* Smart page numbers */}
            {Array.from({ length: totalPages }).map((_, i) => {
              const pageNum = i + 1;
              if (
                pageNum === 1 ||
                pageNum === totalPages ||
                (pageNum >= safePage - 1 && pageNum <= safePage + 1)
              ) {
                return (
                  <button
                    key={pageNum}
                    type="button"
                    onClick={() => setPage(pageNum)}
                    className={`w-8 h-8 flex items-center justify-center rounded-xl text-xs font-bold transition-all cursor-pointer ${
                      safePage === pageNum
                        ? 'bg-primary-500 text-white shadow-xs'
                        : 'border border-background-200/90 text-foreground-700 hover:bg-background-100'
                    }`}
                  >
                    {pageNum}
                  </button>
                );
              }
              if (pageNum === safePage - 2 || pageNum === safePage + 2) {
                return (
                  <span key={pageNum} className="w-6 text-center text-foreground-400 text-xs">
                    &hellip;
                  </span>
                );
              }
              return null;
            })}

            <button
              type="button"
              onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
              disabled={safePage >= totalPages}
              className="w-8 h-8 flex items-center justify-center rounded-xl border border-background-200/90 text-foreground-600 hover:bg-background-100 disabled:opacity-30 disabled:cursor-not-allowed transition-colors cursor-pointer"
              title="Next page"
              aria-label="Next page"
            >
              <i className="ri-arrow-right-s-line text-sm"></i>
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
